<template>
  <div class="sms-gateway" :class="{ embedded }">
    <div v-if="!embedded" class="page-header">
      <h2>短信管理</h2>
      <div class="header-actions">
        <el-input v-model="searchQuery" placeholder="搜索短信内容..." size="default" style="width: 240px" clearable @input="onSearch">
          <template #prefix><span style="color: #a2adbe">⌕</span></template>
        </el-input>
        <el-button type="primary" @click="showRobotDialog(true)" size="default">
          钉钉配置
        </el-button>
        <el-button @click="loadDevices" size="default">刷新设备</el-button>
      </div>
    </div>

    <div v-else class="embed-toolbar">
      <el-input
        v-if="showMessages"
        v-model="searchQuery"
        placeholder="搜索短信内容..."
        size="default"
        style="width: 240px"
        clearable
        @input="onSearch"
      >
        <template #prefix><span style="color: #a2adbe">⌕</span></template>
      </el-input>
      <el-button type="primary" @click="showRobotDialog(true)" size="default">钉钉配置</el-button>
      <el-button @click="loadDevices" size="default">刷新设备</el-button>
    </div>

    <el-tabs v-model="activeTab" class="sms-tabs" :class="{ 'sms-tabs--solo': only !== 'all' }">
      <el-tab-pane v-if="showDevices" label="手机设备" name="devices">
        <el-table :data="devices" stripe v-loading="devicesLoading">
          <el-table-column label="设备名称" prop="name" min-width="180" />
          <el-table-column label="设备 ID" prop="device_id" min-width="150" />
          <el-table-column label="连接地址" prop="base_url" min-width="220" />
          <el-table-column label="设备参数" min-width="330">
            <template #default="{ row }">
              <div class="device-params">
                <div><b>{{ row.device_params?.device_name || row.name || '未知型号' }}</b>
                  <span v-if="row.device_params?.version_name"> · App {{ row.device_params.version_name }}</span>
                </div>
                <div>
                  电量：{{ row.device_params?.battery?.level || '—' }}
                  <span v-if="row.device_params?.battery?.temperature"> · 温度 {{ row.device_params.battery.temperature }}</span>
                </div>
                <div class="sim-summary">
                  <span v-for="sim in (row.device_params?.sim_cards || [])" :key="sim.slot">
                    SIM{{ sim.slot }}：{{ sim.number || '未识别' }}{{ sim.carrier ? `（${sim.carrier}）` : '' }}
                  </span>
                  <span v-if="!(row.device_params?.sim_cards || []).length">SIM：未读取</span>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="90">
            <template #default="{ row }">
              <el-tag :type="row.status === 'online' ? 'success' : 'danger'" size="small">
                {{ row.status === 'online' ? '在线' : '离线' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="最后同步" width="180">
            <template #default="{ row }">{{ formatTime(row.last_success_at) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="170">
            <template #default="{ row }">
              <el-button text type="primary" size="small" @click="pollDeviceNow(row)">立即同步</el-button>
              <el-button text type="warning" size="small" @click="openSendSms(row)">发短信</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
      <el-tab-pane v-if="showMessages" label="短信列表" name="messages">
        <!-- 分组视图 -->
        <div class="group-table">
          <el-table :data="groupItems" stripe highlight-current-row @row-click="onGroupClick" v-loading="loading">
            <el-table-column label="来源设备" min-width="180">
              <template #default="{ row }">
                <div>{{ row.device_name || '未登记设备' }}</div>
                <div class="mono-text">{{ row.device_id || row.phone }}</div>
              </template>
            </el-table-column>
            <el-table-column label="短信会话" width="100">
              <template #default="{ row }">
                <el-tag size="small" type="info">{{ row.session_count || 0 }} 个</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="短信数量" prop="count" width="100">
              <template #default="{ row }">
                <el-tag size="small" type="info">{{ row.count }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="最近一条预览" min-width="300">
              <template #default="{ row }">
                <span class="preview-text">{{ row.preview }}</span>
              </template>
            </el-table-column>
            <el-table-column label="最近收到" width="150">
              <template #default="{ row }">
                <span class="time-text">{{ formatRelative(row.last_time) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="200" fixed="right">
              <template #default="{ row }">
                <el-button size="small" text type="primary" @click.stop="onGroupClick(row)">查看</el-button>
                <el-button size="small" text type="primary" @click.stop="openRules(row.phone)">规则</el-button>
                <el-popconfirm title="确定删除该号码所有短信？" @confirm="deletePhoneGroup(row.phone)">
                  <template #reference>
                    <el-button size="small" text type="danger" @click.stop>删除</el-button>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="pagination-row">
          <el-pagination
            v-model:current-page="groupPage"
            :page-size="groupPageSize"
            :total="groupTotal"
            layout="prev, pager, next, total"
            @current-change="loadGroups"
          />
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- 号码详情抽屉 -->
    <el-drawer v-model="detailVisible" :title="'本机号码：' + detailPhone" size="65%" destroy-on-close>
      <div class="detail-toolbar">
        <el-button size="small" @click="refreshDetail" :icon="Refresh">刷新</el-button>
        <el-button size="small" type="primary" @click="openRules(detailPhone)">规则配置</el-button>
        <el-button size="small" type="warning" @click="reprocessPhone">重新处理</el-button>
        <el-button size="small" @click="exportPhone">导出</el-button>
        <el-popconfirm title="确定删除该号码所有短信？" @confirm="deletePhoneGroup(detailPhone)">
          <template #reference>
            <el-button size="small" type="danger">删除全部</el-button>
          </template>
        </el-popconfirm>
      </div>

      <el-collapse v-model="openSessions" class="session-list" v-loading="detailLoading">
        <el-collapse-item v-for="session in sessions" :key="session.sender_phone" :name="session.sender_phone">
          <template #title>
            <div class="session-title">
              <span class="session-phone-label">会话手机号：</span>
              <span class="session-phone">{{ session.sender_phone }}</span>
              <el-tag size="small" type="info">{{ session.count }} 条短信</el-tag>
              <span class="session-last">{{ formatTime(session.last_time) }}</span>
            </div>
          </template>
          <el-table :data="session.messages" stripe size="small">
            <el-table-column label="类型" width="70">
              <template #default="{ row }">{{ row.sms_type === 2 ? '发送' : '接收' }}</template>
            </el-table-column>
            <el-table-column label="对方手机号" width="160">
              <template #default="{ row }">
                <span class="mono-text">{{ row.sender_phone || session.sender_phone }}</span>
              </template>
            </el-table-column>
            <el-table-column label="归属卡" width="210">
              <template #default="{ row }">
                <span v-if="row.sim_info || (row.sim_slot !== '' && row.sim_slot !== null && row.sim_slot !== undefined)">
                  <span>{{ row.sms_type === 2 ? '发送卡' : '接收卡' }}：</span>
                  {{ row.sim_info || (row.sim_slot !== '' ? `SIM${Number(row.sim_slot) + 1}` : '未识别') }}
                  <span v-if="row.sim_phone">（{{ row.sim_phone }}）</span>
                </span>
                <span v-else class="text-muted">{{ row.sms_type === 2 ? '发送卡：未识别' : '接收卡：未识别' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="内容" min-width="300">
              <template #default="{ row }">
                <div class="msg-content" @click="showMsgDetail(row)">
                  <span class="msg-preview">{{ (row.content || '').slice(0, 80) }}{{ (row.content || '').length > 80 ? '...' : '' }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="处理结果" width="180">
              <template #default="{ row }">
                <span v-if="row.process_result" class="result-text">{{ row.process_result }}</span>
                <span v-else class="text-muted">—</span>
              </template>
            </el-table-column>
            <el-table-column label="钉钉" width="70">
              <template #default="{ row }">
                <el-tag v-if="row.dingtalk_status === 'sent'" size="small" type="success">已发</el-tag>
                <el-tag v-else-if="row.dingtalk_status === 'failed'" size="small" type="danger">失败</el-tag>
                <span v-else class="text-muted">—</span>
              </template>
            </el-table-column>
            <el-table-column label="时间" width="170">
              <template #default="{ row }">{{ formatTime(row.receive_time || row.server_time) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="70" fixed="right">
              <template #default="{ row }">
                <el-button size="small" text type="danger" @click="deleteMsg(row.id)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>
      </el-collapse>
      <!-- 保留分页，避免一次加载超大设备的全部短信 -->
      <el-table v-if="false" :data="detailItems">
        <el-table-column label="ID" prop="id" width="70" />
        <el-table-column label="内容" min-width="300">
          <template #default="{ row }">
            <div class="msg-content" @click="showMsgDetail(row)">
              <span class="msg-preview">{{ (row.content || '').slice(0, 80) }}{{ (row.content || '').length > 80 ? '...' : '' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="处理结果" width="180">
          <template #default="{ row }">
            <span v-if="row.process_result" class="result-text">{{ row.process_result }}</span>
            <span v-else class="text-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="规则" prop="matched_rule" width="120" />
        <el-table-column label="钉钉" width="70">
          <template #default="{ row }">
            <el-tag v-if="row.dingtalk_status === 'sent'" size="small" type="success">已发</el-tag>
            <el-tag v-else-if="row.dingtalk_status === 'failed'" size="small" type="danger">失败</el-tag>
            <span v-else class="text-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="时间" width="170">
          <template #default="{ row }">
            <span class="time-text">{{ formatTime(row.receive_time || row.server_time) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="70" fixed="right">
          <template #default="{ row }">
            <el-button size="small" text type="danger" @click="deleteMsg(row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="false" class="pagination-row">
        <el-pagination
          v-model:current-page="detailPage"
          :page-size="detailPageSize"
          :total="detailTotal"
          layout="prev, pager, next, total"
          @current-change="loadDetail"
        />
      </div>
    </el-drawer>

    <!-- 短信详情弹窗 -->
    <el-dialog v-model="msgDetailVisible" title="短信详情" width="580px" destroy-on-close>
      <div class="msg-detail">
        <div class="msg-field"><label>本机号码：</label><span>{{ msgDetail.device_phone || msgDetail.phone }}</span></div>
        <div class="msg-field"><label>来电手机：</label><span>{{ msgDetail.sender_phone || '—' }}</span></div>
        <div class="msg-field"><label>{{ msgDetail.sms_type === 2 ? '发送卡：' : '接收卡：' }}</label><span>{{ msgDetail.sim_info || (msgDetail.sim_slot !== '' ? `SIM${Number(msgDetail.sim_slot) + 1}` : '—') }}{{ msgDetail.sim_phone ? `（${msgDetail.sim_phone}）` : '' }}</span></div>
        <div class="msg-field"><label>运营商：</label><span>{{ msgDetail.carrier || '—' }}</span></div>
        <div class="msg-field"><label>接收时间：</label><span>{{ formatTime(msgDetail.receive_time) }}</span></div>
        <div class="msg-field"><label>原始内容：</label></div>
        <pre class="msg-raw">{{ msgDetail.content }}</pre>
        <div class="msg-field"><label>验证码：</label><span class="code-text">{{ msgDetail.verification_code || '—' }}</span></div>
        <div class="msg-field" v-if="msgDetail.process_result"><label>处理结果：</label></div>
        <pre class="msg-processed" v-if="msgDetail.process_result">{{ msgDetail.process_result }}</pre>
        <div class="msg-field"><label>匹配规则：</label><span>{{ msgDetail.matched_rule || '—' }}</span></div>
        <div class="msg-field"><label>钉钉状态：</label><span>{{ statusLabel(msgDetail.dingtalk_status) }}</span></div>
        <div class="msg-field" v-if="msgDetail.error_message"><label>错误：</label><span class="text-danger">{{ msgDetail.error_message }}</span></div>
      </div>
    </el-dialog>

    <!-- 规则管理弹窗 -->
    <el-dialog v-model="rulesVisible" :title="'规则管理 — ' + rulesPhone" width="800px" destroy-on-close>
      <div class="rules-toolbar">
        <el-button type="primary" size="small" @click="showRuleForm(null)">添加规则</el-button>
      </div>
      <el-table :data="rulesItems" stripe size="small" v-loading="rulesLoading">
        <el-table-column label="名称" prop="name" width="150" />
        <el-table-column label="条件" min-width="220">
          <template #default="{ row }">
            <span v-if="row.match_contains" class="rule-tag">包含：{{ row.match_contains }}</span>
            <span v-if="row.match_not_contains" class="rule-tag warn">不含：{{ row.match_not_contains }}</span>
            <span v-if="row.match_regex" class="rule-tag info">正则：{{ row.match_regex }}</span>
          </template>
        </el-table-column>
        <el-table-column label="动作" width="120">
          <template #default="{ row }">
            <el-tag size="small">{{ actionLabel(row.action_type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="优先级" prop="priority" width="80" />
        <el-table-column label="启用" width="70">
          <template #default="{ row }">
            <el-switch v-model="row.enabled" size="small" @change="toggleRule(row)" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140">
          <template #default="{ row }">
            <el-button size="small" text type="primary" @click="showRuleForm(row)">编辑</el-button>
            <el-popconfirm title="确定删除？" @confirm="deleteRule(row.id)">
              <template #reference>
                <el-button size="small" text type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <!-- 规则编辑弹窗 -->
    <el-dialog v-model="ruleFormVisible" :title="ruleFormId ? '编辑规则' : '添加规则'" width="620px" destroy-on-close @closed="ruleFormRef?.resetFields()">
      <el-form ref="ruleFormRef" :model="ruleForm" label-width="110px" size="default">
        <el-form-item label="规则名称" required>
          <el-input v-model="ruleForm.name" placeholder="如：提取验证码" />
        </el-form-item>
        <el-form-item label="适用来电手机">
          <el-input v-model="ruleForm.phone" :placeholder="'当前：' + rulesPhone" />
          <div class="form-hint">留空使用当前号码，填 * 匹配所有号码</div>
        </el-form-item>
        <el-form-item label="优先级">
          <el-input-number v-model="ruleForm.priority" :min="0" :max="100" />
        </el-form-item>
        <el-form-item label="包含关键词">
          <el-input v-model="ruleForm.match_contains" placeholder="多个关键词用逗号分隔" />
        </el-form-item>
        <el-form-item label="不包含关键词">
          <el-input v-model="ruleForm.match_not_contains" placeholder="多个关键词用逗号分隔" />
        </el-form-item>
        <el-form-item label="正则表达式">
          <el-input v-model="ruleForm.match_regex" placeholder="如：验证码" />
        </el-form-item>
        <el-form-item label="处理动作" required>
          <el-select v-model="ruleForm.action_type" style="width: 100%">
            <el-option label="提取验证码" value="extract_code" />
            <el-option label="提取金额" value="extract_amount" />
            <el-option label="提取订单号" value="extract_order" />
            <el-option label="提取快递单号" value="extract_express" />
            <el-option label="文本替换" value="text_replace" />
            <el-option label="自定义正则提取" value="custom_regex" />
            <el-option label="自定义模板" value="custom_template" />
            <el-option label="仅保存不转发" value="save_only" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="ruleForm.action_type === 'custom_template'" label="模板">
          <el-input v-model="ruleForm.template" type="textarea" :rows="3" placeholder="平台验证码：{{验证码}}" />
          <div class="form-hint">用双花括号引用提取字段</div>
        </el-form-item>
        <el-form-item label="转发钉钉">
          <el-select v-model="ruleForm.dingtalk_robot_id" clearable placeholder="选择钉钉群" style="width: 100%">
            <el-option v-for="r in robotsList" :key="r.id" :label="r.name + (r.enabled ? '' : ' (已禁用)')" :value="r.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="发送内容">
          <el-radio-group v-model="ruleForm.dingtalk_send_content">
            <el-radio value="both">原文+处理结果</el-radio>
            <el-radio value="raw">仅原文</el-radio>
            <el-radio value="processed">仅处理结果</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="ruleForm.enabled" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="ruleFormVisible = false">取消</el-button>
        <el-button type="primary" @click="saveRule">保存</el-button>
      </template>
    </el-dialog>

    <!-- 钉钉机器人管理弹窗 -->
    <el-dialog v-model="robotVisible" title="钉钉机器人管理" width="700px" destroy-on-close>
      <div class="rules-toolbar">
        <el-button type="primary" size="small" @click="showRobotForm(null)">添加机器人</el-button>
      </div>
      <el-table :data="robotsList" stripe size="small" v-loading="robotsLoading">
        <el-table-column label="名称" prop="name" width="150" />
        <el-table-column label="Webhook" min-width="280">
          <template #default="{ row }">
            <span class="mono-text">{{ (row.webhook || '').slice(0, 60) }}...</span>
          </template>
        </el-table-column>
        <el-table-column label="Secret" width="120">
          <template #default="{ row }">
            <span class="mono-text">{{ row.secret }}</span>
          </template>
        </el-table-column>
        <el-table-column label="启用" width="70">
          <template #default="{ row }">
            <el-switch v-model="row.enabled" size="small" @change="toggleRobot(row)" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200">
          <template #default="{ row }">
            <el-button size="small" text type="primary" @click="showRobotForm(row)">编辑</el-button>
            <el-button size="small" text type="warning" @click="testRobot(row.id)">测试</el-button>
            <el-popconfirm title="确定删除？" @confirm="deleteRobot(row.id)">
              <template #reference>
                <el-button size="small" text type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <!-- 机器人编辑弹窗 -->
    <el-dialog v-model="robotFormVisible" :title="robotFormId ? '编辑机器人' : '添加机器人'" width="500px" destroy-on-close>
      <el-form :model="robotForm" label-width="100px" size="default">
        <el-form-item label="名称" required>
          <el-input v-model="robotForm.name" placeholder="如：运维群" />
        </el-form-item>
        <el-form-item label="Webhook" required>
          <el-input v-model="robotForm.webhook" placeholder="钉钉机器人 Webhook URL" />
        </el-form-item>
        <el-form-item label="加签 Secret">
          <el-input v-model="robotForm.secret" placeholder="可选，安全建议设置" type="password" show-password />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="robotForm.enabled" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="robotFormVisible = false">取消</el-button>
        <el-button type="primary" @click="saveRobot">保存</el-button>
      </template>
    </el-dialog>
    <!-- 发短信弹窗 -->
    <el-dialog v-model="sendSmsVisible" :title="'发送短信 — ' + (sendSmsDevice?.name || sendSmsDevice?.device_id || '')" width="560px" destroy-on-close>
      <el-alert type="warning" :closable="false" show-icon style="margin-bottom: 14px"
        title="注意：发送短信可能产生运营商费用，请确认使用正确的查询指令。" />
      <el-form :model="sendSmsForm" label-width="100px" size="default">
        <el-form-item label="使用卡槽" required>
          <el-select v-model="sendSmsForm.sim_slot" style="width: 100%">
            <el-option label="SIM1" :value="1" />
            <el-option label="SIM2" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item label="接收号码" required>
          <el-input v-model="sendSmsForm.phone_numbers" placeholder="如 10086 / 10010 / 10000" />
        </el-form-item>
        <el-form-item label="短信内容" required>
          <el-input v-model="sendSmsForm.msg_content" type="textarea" :rows="3" placeholder="如 YE（查余额）/ CXLL（查流量）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="sendSmsVisible = false">取消</el-button>
        <el-button type="primary" :loading="sendSmsLoading" @click="submitSendSms">发送</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'
import { smsApi } from '@/api'

export default {
  name: 'SmsGateway',
  components: {},
  props: {
    embedded: { type: Boolean, default: false },
    only: { type: String, default: 'all' },
  },
  setup(props) {
    const showDevices = computed(() => props.only !== 'messages')
    const showMessages = computed(() => props.only !== 'devices')
    const activeTab = ref(props.only === 'devices' ? 'devices' : 'messages')
    const devices = ref([])
    const devicesLoading = ref(false)

    async function loadDevices() {
      devicesLoading.value = true
      try {
        const res = await smsApi.listDevices()
        devices.value = res.items || []
      } catch (e) {
        ElMessage.error('加载设备失败')
      } finally {
        devicesLoading.value = false
      }
    }

    async function pollDeviceNow(row) {
      try {
        await smsApi.pollDevice(row.id)
        ElMessage.success('同步完成')
        loadDevices()
        loadGroups()
      } catch (e) {
        ElMessage.error('同步失败')
      }
    }

    // 发送短信
    const sendSmsVisible = ref(false)
    const sendSmsLoading = ref(false)
    const sendSmsDevice = ref(null)
    const sendSmsForm = ref({ sim_slot: 1, phone_numbers: '', msg_content: '' })

    function openSendSms(row) {
      sendSmsDevice.value = row
      sendSmsForm.value = { sim_slot: 1, phone_numbers: '', msg_content: '' }
      sendSmsVisible.value = true
    }

    async function submitSendSms() {
      if (!sendSmsDevice.value || !sendSmsForm.value.msg_content.trim()) {
        ElMessage.warning('请填写短信内容')
        return
      }
      sendSmsLoading.value = true
      try {
        await smsApi.sendSms(sendSmsDevice.value.id, sendSmsForm.value)
        ElMessage.success('发送成功')
        sendSmsVisible.value = false
      } catch (e) {
        ElMessage.error('发送失败: ' + (e.message || ''))
      } finally {
        sendSmsLoading.value = false
      }
    }
    const searchQuery = ref('')
    const loading = ref(false)

    // ── 分组视图 ──
    const groupItems = ref([])
    const groupPage = ref(1)
    const groupPageSize = ref(20)
    const groupTotal = ref(0)

    async function loadGroups() {
      loading.value = true
      try {
        const params = { skip: (groupPage.value - 1) * groupPageSize.value, limit: groupPageSize.value }
        if (searchQuery.value) params.search = searchQuery.value
        const res = await smsApi.listGroups(params)
        groupItems.value = res.items || []
        groupTotal.value = res.total || 0
      } catch (e) {
        ElMessage.error('加载失败: ' + (e.message || '未知错误'))
      } finally {
        loading.value = false
      }
    }

    function onSearch() { groupPage.value = 1; loadGroups() }
    let searchTimer = null
    watch(searchQuery, () => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => { groupPage.value = 1; loadGroups() }, 400)
    })

    // ── 详情抽屉 ──
    const detailVisible = ref(false)
    const detailPhone = ref('')
    const detailDeviceName = ref('')
    const detailItems = ref([])
    const sessions = ref([])
    const openSessions = ref([])
    const detailPage = ref(1)
    const detailPageSize = ref(50)
    const detailTotal = ref(0)
    const detailLoading = ref(false)

    function onGroupClick(row) {
      detailPhone.value = row.phone
      detailDeviceName.value = row.device_name || row.device_id || row.phone
      detailPage.value = 1
      detailVisible.value = true
      loadDetail()
    }

    async function loadDetail() {
      detailLoading.value = true
      try {
        const sessionRes = await smsApi.listSessions(detailPhone.value)
        sessions.value = await Promise.all((sessionRes.items || []).map(async (session) => {
          const res = await smsApi.listByPhone(detailPhone.value, {
            sender_phone: session.sender_phone,
            skip: 0,
            limit: 1000,
          })
          return { ...session, messages: res.items || [] }
        }))
        openSessions.value = sessions.value.length ? [sessions.value[0].sender_phone] : []
      } catch (e) {
        ElMessage.error('加载失败')
      } finally {
        detailLoading.value = false
      }
    }

    function refreshDetail() { loadDetail() }

    async function reprocessPhone() {
      try {
        const res = await smsApi.reprocess(detailPhone.value)
        ElMessage.success(res.message)
        loadDetail()
      } catch (e) {
        ElMessage.error('处理失败')
      }
    }

    function exportPhone() {
      window.open('/api/sms/export?phone=' + detailPhone.value, '_blank')
    }

    async function deletePhoneGroup(phone) {
      try {
        await smsApi.deleteByPhone(phone)
        ElMessage.success('已删除')
        if (detailVisible.value && detailPhone.value === phone) detailVisible.value = false
        loadGroups()
      } catch (e) {
        ElMessage.error('删除失败')
      }
    }

    async function deleteMsg(id) {
      try {
        await smsApi.deleteMessage(id)
        ElMessage.success('已删除')
        loadDetail()
      } catch (e) {
        ElMessage.error('删除失败')
      }
    }

    // ── 短信详情弹窗 ──
    const msgDetailVisible = ref(false)
    const msgDetail = ref({})

    function showMsgDetail(row) {
      msgDetail.value = row
      msgDetailVisible.value = true
    }

    // ── 规则管理 ──
    const rulesVisible = ref(false)
    const rulesPhone = ref('')
    const rulesItems = ref([])
    const rulesLoading = ref(false)

    async function openRules(phone) {
      rulesPhone.value = phone
      rulesVisible.value = true
      await loadRules()
    }

    async function loadRules() {
      rulesLoading.value = true
      try {
        const res = await smsApi.listRules(rulesPhone.value)
        rulesItems.value = res.items || []
      } catch (e) {
        ElMessage.error('加载规则失败')
      } finally {
        rulesLoading.value = false
      }
    }

    const ruleFormVisible = ref(false)
    const ruleFormId = ref(null)
    const ruleFormRef = ref(null)
    const ruleForm = ref({
      phone: '', name: '', priority: 0, enabled: true,
      match_contains: '', match_not_contains: '', match_regex: '',
      action_type: 'save_only', action_config: {}, template: '',
      dingtalk_robot_id: null, dingtalk_send_content: 'both',
    })

    function showRuleForm(row) {
      if (row) {
        ruleFormId.value = row.id
        ruleForm.value = {
          phone: row.phone || '', name: row.name || '', priority: row.priority || 0,
          enabled: row.enabled, match_contains: row.match_contains || '',
          match_not_contains: row.match_not_contains || '', match_regex: row.match_regex || '',
          action_type: row.action_type || 'save_only',
          action_config: row.action_config || {}, template: row.template || '',
          dingtalk_robot_id: row.dingtalk_robot_id || null,
          dingtalk_send_content: row.dingtalk_send_content || 'both',
        }
      } else {
        ruleFormId.value = null
        ruleForm.value = {
          phone: rulesPhone.value, name: '', priority: 0, enabled: true,
          match_contains: '', match_not_contains: '', match_regex: '',
          action_type: 'save_only', action_config: {}, template: '',
          dingtalk_robot_id: null, dingtalk_send_content: 'both',
        }
      }
      ruleFormVisible.value = true
    }

    async function saveRule() {
      try {
        if (ruleFormId.value) {
          await smsApi.updateRule(ruleFormId.value, ruleForm.value)
          ElMessage.success('规则已更新')
        } else {
          await smsApi.createRule(ruleForm.value)
          ElMessage.success('规则已创建')
        }
        ruleFormVisible.value = false
        loadRules()
      } catch (e) {
        ElMessage.error('保存失败: ' + (e.message || ''))
      }
    }

    async function deleteRule(id) {
      try {
        await smsApi.deleteRule(id)
        ElMessage.success('已删除')
        loadRules()
      } catch (e) {
        ElMessage.error('删除失败')
      }
    }

    async function toggleRule(row) {
      try {
        await smsApi.updateRule(row.id, {
          phone: row.phone, name: row.name, priority: row.priority, enabled: row.enabled,
          match_contains: row.match_contains || '', match_not_contains: row.match_not_contains || '',
          match_regex: row.match_regex || '', action_type: row.action_type || 'save_only',
          action_config: row.action_config || {}, template: row.template || '',
          dingtalk_robot_id: row.dingtalk_robot_id, dingtalk_send_content: row.dingtalk_send_content || 'both',
        })
      } catch (e) {
        row.enabled = !row.enabled
      }
    }

    // ── 钉钉机器人 ──
    const robotVisible = ref(false)
    const robotsList = ref([])
    const robotsLoading = ref(false)

    async function loadRobots() {
      robotsLoading.value = true
      try {
        const res = await smsApi.listRobots()
        robotsList.value = res.items || []
      } catch (e) {
        ElMessage.error('加载机器人失败')
      } finally {
        robotsLoading.value = false
      }
    }

    function showRobotDialog() {
      robotVisible.value = true
      loadRobots()
    }

    const robotFormVisible = ref(false)
    const robotFormId = ref(null)
    const robotForm = ref({ name: '', webhook: '', secret: '', enabled: true })

    function showRobotForm(row) {
      if (row) {
        robotFormId.value = row.id
        robotForm.value = { name: row.name, webhook: row.webhook, secret: '', enabled: row.enabled }
        // load full secret
        smsApi.getRobot(row.id).then(res => { robotForm.value.secret = res.secret }).catch(() => {})
      } else {
        robotFormId.value = null
        robotForm.value = { name: '', webhook: '', secret: '', enabled: true }
      }
      robotFormVisible.value = true
    }

    async function saveRobot() {
      try {
        if (robotFormId.value) {
          await smsApi.updateRobot(robotFormId.value, robotForm.value)
          ElMessage.success('机器人已更新')
        } else {
          await smsApi.createRobot(robotForm.value)
          ElMessage.success('机器人已创建')
        }
        robotFormVisible.value = false
        loadRobots()
      } catch (e) {
        ElMessage.error('保存失败: ' + (e.message || ''))
      }
    }

    async function deleteRobot(id) {
      try {
        await smsApi.deleteRobot(id)
        ElMessage.success('已删除')
        loadRobots()
      } catch (e) {
        ElMessage.error('删除失败')
      }
    }

    async function toggleRobot(row) {
      try {
        await smsApi.updateRobot(row.id, { name: row.name, webhook: row.webhook, secret: '', enabled: row.enabled })
      } catch (e) {
        row.enabled = !row.enabled
      }
    }

    async function testRobot(id) {
      try {
        await smsApi.testRobot(id)
        ElMessage.success('测试消息发送成功')
      } catch (e) {
        ElMessage.error('发送失败: ' + (e.response?.data?.message || e.message || ''))
      }
    }

    // ── 工具函数 ──
    function formatTime(t) {
      if (!t) return ''
      const d = new Date(t)
      return d.toLocaleString('zh-CN', { hour12: false })
    }

    function formatRelative(t) {
      if (!t) return ''
      const now = Date.now()
      const then = new Date(t).getTime()
      const diff = Math.floor((now - then) / 1000)
      if (diff < 60) return '刚刚'
      if (diff < 3600) return Math.floor(diff / 60) + ' 分钟前'
      if (diff < 86400) return Math.floor(diff / 3600) + ' 小时前'
      if (diff < 172800) return '昨天'
      return Math.floor(diff / 86400) + ' 天前'
    }

    function actionLabel(type) {
      const map = {
        extract_code: '提取验证码', extract_amount: '提取金额',
        extract_order: '提取订单号', extract_express: '提取快递单号',
        text_replace: '文本替换', custom_regex: '自定义正则',
        custom_template: '自定义模板', save_only: '仅保存',
      }
      return map[type] || type
    }

    function statusLabel(s) {
      const map = { sent: '已发送', failed: '发送失败', none: '未发送', pending: '待发送' }
      return map[s] || s || '—'
    }

    onMounted(() => {
      loadDevices()
      loadGroups()
    })

    return {
      embedded: computed(() => props.embedded),
      only: computed(() => props.only),
      showDevices,
      showMessages,
      activeTab, searchQuery, loading, devices, devicesLoading, loadDevices, pollDeviceNow,
      sendSmsVisible, sendSmsLoading, sendSmsDevice, sendSmsForm, openSendSms, submitSendSms,
      groupItems, groupPage, groupPageSize, groupTotal, loadGroups, onSearch, onGroupClick, detailDeviceName,
      detailVisible, detailPhone, detailItems, sessions, openSessions, detailPage, detailPageSize, detailTotal, detailLoading, loadDetail, refreshDetail,
      deletePhoneGroup, deleteMsg, reprocessPhone, exportPhone,
      msgDetailVisible, msgDetail, showMsgDetail,
      rulesVisible, rulesPhone, rulesItems, rulesLoading, openRules,
      ruleFormVisible, ruleFormId, ruleFormRef, ruleForm, showRuleForm, saveRule, deleteRule, toggleRule,
      robotVisible, robotsList, robotsLoading, showRobotDialog,
      robotFormVisible, robotFormId, robotForm, showRobotForm, saveRobot, deleteRobot, toggleRobot, testRobot,
      formatTime, formatRelative, actionLabel, statusLabel,
      Refresh,
    }
  },
}
</script>

<style scoped>
.sms-gateway { padding: 0; }
.embed-toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.sms-tabs--solo :deep(.el-tabs__header) { display: none; }
.session-list { margin-top: 12px; }
.session-title { display: flex; align-items: center; gap: 12px; width: 100%; padding-right: 18px; }
.session-phone-label { color: var(--mp-text-secondary); }
.session-phone { color: var(--mp-text); font-weight: 600; font-family: monospace; }
.session-last { margin-left: auto; color: var(--mp-text-secondary); font-size: 12px; }
.device-params { line-height: 1.55; font-size: 12px; color: var(--mp-text-secondary); }
.device-params b { color: var(--mp-text); }
.sim-summary { display: flex; flex-wrap: wrap; gap: 4px 12px; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; }
.page-header h2 { font-size: 18px; font-weight: 700; color: var(--mp-text); margin: 0; }
.header-actions { display: flex; gap: 10px; align-items: center; }
.sms-tabs { margin-top: 4px; }
.group-table { margin-top: 8px; }
.preview-text { color: var(--mp-text-secondary); font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; max-width: 100%; }
.time-text { color: var(--mp-text-muted); font-size: 11px; white-space: nowrap; }
.text-muted { color: #c0c4cc; }
.text-danger { color: #f56c6c; }
.code-text { color: #e6a23c; font-size: 18px; font-weight: 800; letter-spacing: 2px; }
.pagination-row { margin-top: 14px; display: flex; justify-content: flex-end; }

.detail-toolbar { display: flex; gap: 8px; margin-bottom: 14px; flex-wrap: wrap; }
.msg-content { cursor: pointer; }
.msg-preview { color: var(--mp-text-secondary); font-size: 12px; line-height: 1.5; }
.result-text { color: var(--mp-success); font-size: 12px; font-weight: 600; }

.msg-detail { font-size: 13px; }
.msg-field { margin-bottom: 8px; }
.msg-field label { font-weight: 600; color: var(--mp-text); margin-right: 6px; }
.msg-raw, .msg-processed { background: #f5f7fa; border-radius: 6px; padding: 12px 14px; font-size: 12px; line-height: 1.6; white-space: pre-wrap; word-break: break-all; margin: 6px 0 12px; color: var(--mp-text); }
.msg-processed { background: #f0f9eb; color: #67c23a; }

.rules-toolbar { margin-bottom: 12px; }
.rule-tag { display: inline-block; background: var(--mp-accent-soft); color: var(--mp-accent); font-size: 11px; padding: 2px 7px; border-radius: 4px; margin-right: 6px; margin-bottom: 3px; }
.rule-tag.warn { background: #fef0f0; color: #f56c6c; }
.rule-tag.info { background: #ecf5ff; color: #409eff; }
.mono-text { font-family: var(--mp-font-mono); font-size: 11px; color: var(--mp-text-secondary); }
.form-hint { font-size: 11px; color: #c0c4cc; margin-top: 4px; }
</style>